import { useEffect, useRef, useState } from "react";
import DOMPurify from "isomorphic-dompurify";
import { api } from "@/lib/api";
import { PageHeader, Section, Btn, Input, Select } from "@/components/Shared";
import { VisualHtmlEditor } from "@/components/VisualHtmlEditor";
import { useBranding } from "@/lib/branding";
import { useAuth } from "@/lib/auth";
import { openConfirm } from "@/lib/dialogs";
import {
  Mail, CheckCircle2, AlertCircle, KeyRound, Send, RefreshCw,
  Palette, Settings as SettingsIcon, ListChecks, Upload, Image as ImageIcon, X,
  Sparkles, ShieldCheck, ShieldOff, Lock, Eye, EyeOff, Users as UsersIcon, Trash2,
  Tag, Edit2, FileText, Send as SendIcon, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const THEMES = [
  { value: "default",  label: "BizAssist Blue (default)", swatch: "#2563EB" },
  { value: "indigo",   label: "Indigo",   swatch: "#4F46E5" },
  { value: "teal",     label: "Teal",     swatch: "#0D9488" },
  { value: "emerald",  label: "Emerald",  swatch: "#059669" },
  { value: "cyan",     label: "Cyan",     swatch: "#0891B2" },
  { value: "plum",     label: "Plum",     swatch: "#7C3AED" },
  { value: "rose",     label: "Rose",     swatch: "#E11D48" },
  { value: "sand",     label: "Sand",     swatch: "#B45309" },
  { value: "slate",    label: "Slate",    swatch: "#475569" },
];

const TABS = [
  { id: "general",   label: "General & Branding", icon: SettingsIcon },
  { id: "email",     label: "API Configuration",  icon: Mail },
  { id: "templates", label: "Email Templates",    icon: FileText },
  { id: "access",    label: "Portal Access",       icon: UsersIcon },
  { id: "master",    label: "Master Data",         icon: Tag },
  { id: "logs",      label: "Email Send Log",     icon: ListChecks },
];

const MAX_LOGO_BYTES = 256 * 1024; // 256 KB cap on the base64 payload

export default function Settings() {
  const [tab, setTab] = useState("general");
  const { refresh: refreshBranding } = useBranding();
  const { user } = useAuth();
  const isDemo = !!user?.demo;
  const [demoResetBusy, setDemoResetBusy] = useState(false);
  const [twofaResetBusy, setTwofaResetBusy] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [cpw, setCpw] = useState({ current: "", next: "", confirm: "" });
  const [cpwBusy, setCpwBusy] = useState(false);
  const resetDemo = async () => {
    if (!await openConfirm({
      title: "Reset demo sandbox?",
      message: "All edits you've made to the demo data will be wiped and replaced with fresh random data. Your live account is not affected.",
      confirmLabel: "Reset sandbox",
      danger: true,
    })) return;
    setDemoResetBusy(true);
    try {
      const { data } = await api.post("/demo/reset");
      toast.success(`Demo reset: ${data.stats.merchants} merchants · ${data.stats.partners} partners · ${data.stats.volumes} volume rows`);
      // Force a reload so every page re-fetches from the freshly-seeded demo DB
      setTimeout(() => window.location.reload(), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
      setDemoResetBusy(false);
    }
  };

  const resetTwofa = async () => {
    if (!await openConfirm({
      title: "Reset 2FA for your account?",
      message:
        "Your current authenticator entry will stop working. On your NEXT sign-in you'll be shown a fresh QR to enroll a new device.\n\n" +
        "You will stay signed in on this session — but be sure to enroll BEFORE you sign out, otherwise you won't be able to log in again until enrollment is complete.",
      confirmLabel: "Reset 2FA",
      danger: true,
    })) return;
    setTwofaResetBusy(true);
    try {
      await api.post("/auth/2fa/reset");
      toast.success("2FA reset. Your next sign-in will show a new QR code to enroll your authenticator.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "2FA reset failed");
    }
    setTwofaResetBusy(false);
  };

  const changePassword = async (e) => {
    e?.preventDefault?.();
    if (!cpw.current || !cpw.next || !cpw.confirm) {
      toast.error("Fill all three fields"); return;
    }
    if (cpw.next.length < 10) {
      toast.error("New password must be at least 10 characters"); return;
    }
    if (cpw.next !== cpw.confirm) {
      toast.error("New passwords don't match"); return;
    }
    if (cpw.next === cpw.current) {
      toast.error("New password can't equal current password"); return;
    }
    setCpwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: cpw.current,
        new_password: cpw.next,
      });
      toast.success("Password updated. Use the new password on your next sign-in.");
      setCpw({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change password");
    }
    setCpwBusy(false);
  };

  const [form, setForm] = useState({
    nk_email: "", rr_email: "",
    sender_email: "", sender_name: "BizAssist Online",
    resend_mode: "test",
    app_name: "BizAssist Online",
    logo_data_url: "",
    theme_preset: "default",
  });
  const [keyInput, setKeyInput] = useState("");
  const [keyMasked, setKeyMasked] = useState("");
  const [resendConfigured, setResendConfigured] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [logBusy, setLogBusy] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState({ app_name: "BizAssist Online", logo_data_url: "", theme_preset: "default" });
  const fileRef = useRef(null);

  const loadLog = async () => {
    setLogBusy(true);
    try {
      const { data } = await api.get("/email/log?limit=30");
      setLog(data || []);
    } catch (e) {
      // Phase 1 — log to devtools so silent failures are debuggable;
      // the panel intentionally does not surface a toast for this poll
      // (it would be noisy on every 30s tick if the backend hiccups).
      console.error("Settings: failed to load email log", e);
    }
    setLogBusy(false);
  };

  const load = async () => {
    const { data } = await api.get("/settings");
    setForm({
      nk_email: data.nk_email || "",
      rr_email: data.rr_email || "",
      sender_email: data.sender_email || "",
      sender_name: data.sender_name || "BizAssist Online",
      resend_mode: data.resend_mode || "test",
      app_name: data.app_name || "BizAssist Online",
      logo_data_url: data.logo_data_url || "",
      theme_preset: data.theme_preset || "default",
    });
    setSavedSnapshot({
      app_name: data.app_name || "BizAssist Online",
      logo_data_url: data.logo_data_url || "",
      theme_preset: data.theme_preset || "default",
    });
    setKeyMasked(data.resend_api_key_masked || "");
    setResendConfigured(!!data.resend_configured);
    setKeyInput("");
  };
  useEffect(() => { load(); loadLog(); }, []);

  // Iter31.2 — auto-refresh email log every time the user activates the
  // Logs tab so newly-sent emails appear without a manual Refresh click.
  // The actual fetch is small (limit=30) so polling on tab visit is
  // cheap. Settings tabs are conditionally rendered, so this hook fires
  // on every transition into "logs" — exactly the trigger we want.
  useEffect(() => {
    if (tab === "logs") loadLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Gentle 30s polling while the Logs tab is the active one so freshly-
  // sent emails surface without manual interaction. Skipped on other
  // tabs to avoid pointless network noise.
  useEffect(() => {
    if (tab !== "logs") return undefined;
    const id = setInterval(() => { loadLog(); }, 30_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // Live preview: as soon as the user picks a theme / logo / display name,
  // apply it to the live DOM (data-theme on <html>, document.title, favicon)
  // so they can see it instantly. Save persists; Reset reverts to the
  // last-loaded saved snapshot.
  useEffect(() => {
    const t = form.theme_preset === "default" || !form.theme_preset ? "" : form.theme_preset;
    if (t) document.documentElement.setAttribute("data-theme", t);
    else document.documentElement.removeAttribute("data-theme");
  }, [form.theme_preset]);

  useEffect(() => {
    if (form.app_name) document.title = form.app_name;
  }, [form.app_name]);

  useEffect(() => {
    if (!form.logo_data_url) return;
    let link = document.querySelector("link[rel='icon']");
    if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
    link.href = form.logo_data_url;
  }, [form.logo_data_url]);

  // When the preview-affecting fields differ from the saved values, the user
  // has un-persisted changes worth highlighting.
  const brandingDirty = (
    form.app_name !== savedSnapshot.app_name ||
    form.logo_data_url !== savedSnapshot.logo_data_url ||
    form.theme_preset !== savedSnapshot.theme_preset
  );
  const resetBranding = () => {
    setForm((f) => ({ ...f, ...savedSnapshot }));
  };

  // If the user navigates away without saving, revert the DOM-level preview
  // back to the persisted branding (held by BrandingProvider).
  useEffect(() => {
    return () => {
      if (brandingDirty) {
        const t = savedSnapshot.theme_preset === "default" || !savedSnapshot.theme_preset ? "" : savedSnapshot.theme_preset;
        if (t) document.documentElement.setAttribute("data-theme", t);
        else document.documentElement.removeAttribute("data-theme");
        if (savedSnapshot.app_name) document.title = savedSnapshot.app_name;
        if (savedSnapshot.logo_data_url) {
          let link = document.querySelector("link[rel='icon']");
          if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
          link.href = savedSnapshot.logo_data_url;
        }
        // Also tell BrandingProvider to re-sync from the server (single source of truth).
        refreshBranding();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandingDirty, savedSnapshot]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { ...form };
      if (keyInput.trim()) body.resend_api_key = keyInput.trim();
      await api.put("/settings", body);
      toast.success("Settings saved");
      await load();
      // Re-fetch the public branding subset so the sidebar/title update live.
      refreshBranding();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (e?.message || "Failed");
      toast.error(msg);
    }
    setBusy(false);
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter a recipient email"); return; }
    setTestBusy(true);
    try {
      const res = await api.post("/email/test", { to_email: testEmail.trim() });
      toast.success(`Test email sent to ${res.data.to} · mode: ${res.data.mode}`);
      await loadLog();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (e?.message || "Failed");
      toast.error(msg);
    }
    setTestBusy(false);
  };

  const onLogoFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file (PNG / SVG / JPG)"); return; }
    if (file.size > MAX_LOGO_BYTES) { toast.error("Logo too large — keep it under 256 KB for fast load."); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result;
      if (typeof dataUrl === "string") {
        setForm((f) => ({ ...f, logo_data_url: dataUrl }));
      }
    };
    reader.readAsDataURL(file);
  };
  const clearLogo = () => setForm((f) => ({ ...f, logo_data_url: "" }));

  const isProd = form.resend_mode === "prod";

  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" subtitle="Branding, email provider, and send-log audit trail" />

      <div className="px-4 sm:px-10 pt-6">
        {/* Horizontal tab strip */}
        <div className="flex flex-wrap gap-1 border-b border-[var(--line)]" data-testid="settings-tabs" role="tablist">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                role="tab"
                aria-selected={active}
                data-testid={`settings-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`relative inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-medium border-b-2 -mb-px transition-colors
                  ${active
                    ? "border-[var(--accent)] text-[var(--ink)]"
                    : "border-transparent text-[var(--muted)] hover:text-[var(--ink)] hover:bg-[var(--surface-2)]"}`}
              >
                <Icon size={14} strokeWidth={1.75} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="p-4 sm:p-10 max-w-4xl space-y-5">
        {tab === "general" && (
          <>
            {isDemo && (
              <Section title="Demo sandbox" testid="settings-demo">
                <div className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-sm bg-amber-100 border border-amber-300 grid place-items-center shrink-0">
                      <Sparkles size={15} className="text-amber-700" />
                    </div>
                    <div>
                      <div className="text-[13.5px] font-medium text-[var(--ink)]">You're in the demo sandbox</div>
                      <div className="text-[12px] text-[var(--muted)] mt-0.5 max-w-md">
                        All data here is randomly generated and isolated from your live account. Use this to demo the dashboard without revealing real numbers. Click reset to restart with a fresh random dataset.
                      </div>
                    </div>
                  </div>
                  <Btn variant="accent" testid="demo-reset-btn" onClick={resetDemo} disabled={demoResetBusy}>
                    <RefreshCw size={13} className={demoResetBusy ? "animate-spin" : ""} />
                    {demoResetBusy ? "Resetting…" : "Reset demo data"}
                  </Btn>
                </div>
              </Section>
            )}

            {!isDemo && (
              <Section title="Security" testid="settings-security">
                <div className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-sm bg-[var(--accent)]/10 border border-[var(--accent)]/30 grid place-items-center shrink-0">
                      {user?.has_2fa ? (
                        <ShieldCheck size={15} className="text-[var(--accent)]" />
                      ) : (
                        <ShieldOff size={15} className="text-amber-700" />
                      )}
                    </div>
                    <div>
                      <div className="text-[13.5px] font-medium text-[var(--ink)] flex items-center gap-2">
                        Two-factor authentication
                        {user?.has_2fa ? (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">Enabled</span>
                        ) : (
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-300">Not enrolled</span>
                        )}
                      </div>
                      <div className="text-[12px] text-[var(--muted)] mt-0.5 max-w-md">
                        {user?.has_2fa
                          ? "Reset 2FA if you've lost access to your authenticator device or want to switch apps. Your next sign-in will show a fresh QR code to re-enroll."
                          : "Sign out and back in to enroll a new authenticator app — you'll be shown a QR code to scan."}
                      </div>
                    </div>
                  </div>
                  {user?.has_2fa && (
                    <Btn variant="ghost" testid="security-2fa-reset" onClick={resetTwofa} disabled={twofaResetBusy}>
                      <RefreshCw size={13} className={twofaResetBusy ? "animate-spin" : ""} />
                      {twofaResetBusy ? "Resetting…" : "Reset 2FA"}
                    </Btn>
                  )}
                </div>

                {/* ---- Change password ----------------------------------- */}
                <div className="border-t border-[var(--line)] mt-1">
                  <form onSubmit={changePassword} className="p-5 space-y-4" data-testid="security-change-password">
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-sm bg-[var(--accent)]/10 border border-[var(--accent)]/30 grid place-items-center shrink-0">
                        <Lock size={15} className="text-[var(--accent)]" />
                      </div>
                      <div>
                        <div className="text-[13.5px] font-medium text-[var(--ink)]">Change password</div>
                        <div className="text-[12px] text-[var(--muted)] mt-0.5 max-w-md">
                          You'll stay signed in on this session. Use the new password on your next sign-in.
                          Minimum 10 characters.
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Current password</label>
                        <div className="relative">
                          <Input
                            data-testid="cpw-current"
                            type={showCpw ? "text" : "password"}
                            value={cpw.current}
                            onChange={(e) => setCpw({ ...cpw, current: e.target.value })}
                            autoComplete="current-password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowCpw((v) => !v)}
                            tabIndex={-1}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--ink)] transition-colors"
                            aria-label={showCpw ? "Hide passwords" : "Show passwords"}
                          >
                            {showCpw ? <EyeOff size={13} /> : <Eye size={13} />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">New password</label>
                        <Input
                          data-testid="cpw-next"
                          type={showCpw ? "text" : "password"}
                          value={cpw.next}
                          onChange={(e) => setCpw({ ...cpw, next: e.target.value })}
                          autoComplete="new-password"
                        />
                      </div>
                      <div>
                        <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Confirm new</label>
                        <Input
                          data-testid="cpw-confirm"
                          type={showCpw ? "text" : "password"}
                          value={cpw.confirm}
                          onChange={(e) => setCpw({ ...cpw, confirm: e.target.value })}
                          autoComplete="new-password"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Btn variant="accent" testid="cpw-submit" onClick={changePassword} disabled={cpwBusy}>
                        <KeyRound size={13} />
                        {cpwBusy ? "Updating…" : "Update password"}
                      </Btn>
                    </div>
                  </form>
                </div>
              </Section>
            )}

            {brandingDirty && (
              <div
                data-testid="branding-preview-banner"
                className="flex items-center justify-between gap-3 px-4 py-2.5 bg-[var(--accent)]/10 border border-[var(--accent)]/30 rounded-sm flex-wrap"
              >
                <div className="flex items-center gap-2 text-[12.5px] text-[var(--ink)]">
                  <span className="h-2 w-2 rounded-full bg-[var(--accent)] pulse-dot" />
                  <span><b>Live preview active</b> — reload the page or click Reset to discard. Save to apply across all sessions.</span>
                </div>
                <div className="flex items-center gap-2">
                  <Btn variant="ghost" testid="branding-reset" onClick={resetBranding}>
                    <RefreshCw size={12} /> Reset
                  </Btn>
                  <Btn variant="accent" testid="branding-save" onClick={save} disabled={busy}>
                    <CheckCircle2 size={13} /> {busy ? "Saving…" : "Save preview"}
                  </Btn>
                </div>
              </div>
            )}

            <Section title="App identity" testid="settings-identity">
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Display Name</label>
                  <Input
                    data-testid="settings-app-name"
                    value={form.app_name}
                    onChange={(e) => setForm({ ...form, app_name: e.target.value })}
                    placeholder="BizAssist Online"
                  />
                  <div className="text-[10.5px] text-[var(--muted)] mt-1">Shown in the sidebar header and the browser tab title.</div>
                </div>
                <div>
                  <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center gap-1.5">
                    <Palette size={11} /> Accent theme
                  </label>
                  <div className="grid grid-cols-3 gap-1.5 mt-1.5" data-testid="settings-theme-grid">
                    {THEMES.map((t) => {
                      const active = form.theme_preset === t.value;
                      return (
                        <button
                          key={t.value}
                          type="button"
                          data-testid={`settings-theme-${t.value}`}
                          onClick={() => setForm({ ...form, theme_preset: t.value })}
                          className={`flex items-center gap-2 px-2.5 py-1.5 rounded-sm border text-[11.5px] transition-colors
                            ${active
                              ? "border-[var(--ink)] bg-[var(--ink)] text-white"
                              : "border-[var(--line-2)] bg-[var(--surface)] hover:border-[var(--ink-2)]"}`}
                        >
                          <span className="h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: t.swatch }} />
                          <span className="truncate">{t.label.replace(/\s*\(default\)/, "")}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="text-[10.5px] text-[var(--muted)] mt-2">Click any swatch to preview live across the UI — Save to apply permanently.</div>
                </div>
              </div>
            </Section>

            <Section title="Logo" testid="settings-logo">
              <div className="p-5 flex flex-col sm:flex-row items-start gap-5">
                <div className="h-24 w-24 rounded-sm bg-[var(--ink)] grid place-items-center shrink-0 overflow-hidden border border-[var(--line-2)]" data-testid="settings-logo-preview">
                  {form.logo_data_url ? (
                    <img src={form.logo_data_url} alt="Logo preview" className="h-20 w-20 object-contain" />
                  ) : (
                    <div className="text-white/40 text-[10px] uppercase tracking-wider flex flex-col items-center gap-1.5">
                      <ImageIcon size={20} strokeWidth={1.5} />
                      <span>No logo</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 w-full">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="settings-logo-file"
                    onChange={(e) => onLogoFile(e.target.files?.[0])}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Btn variant="ghost" testid="settings-logo-upload" onClick={() => fileRef.current?.click()}>
                      <Upload size={13} /> Upload logo
                    </Btn>
                    {form.logo_data_url && (
                      <Btn variant="ghost" testid="settings-logo-clear" onClick={clearLogo}>
                        <X size={13} /> Remove
                      </Btn>
                    )}
                  </div>
                  <div className="text-[11px] text-[var(--muted)] mt-2 leading-relaxed">
                    Square PNG/SVG works best. Stored as a base64 data URL — keep it under <span className="num">256 KB</span> for snappy page loads.
                    The logo also becomes the browser favicon.
                  </div>
                </div>
              </div>
            </Section>

            <div className="flex justify-end">
              <Btn variant="accent" testid="settings-save" onClick={save} disabled={busy}>
                <CheckCircle2 size={14} /> {busy ? "Saving…" : "Save settings"}
              </Btn>
            </div>
          </>
        )}

        {tab === "email" && (
          <>
            <Section title="Resend · Transactional Email" testid="settings-resend">
              <div className="p-5 space-y-4">
                <div className="flex items-start gap-3">
                  {resendConfigured ? (
                    <>
                      <div className="h-8 w-8 rounded-full bg-emerald-50 grid place-items-center text-emerald-700 shrink-0"><CheckCircle2 size={16} /></div>
                      <div className="text-[12.5px]">
                        <span className="font-semibold">API key configured</span>{" "}
                        <span className="text-[var(--muted)] num">{keyMasked}</span> · mode:{" "}
                        <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm border ${isProd ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                          {form.resend_mode}
                        </span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="h-8 w-8 rounded-full bg-amber-50 grid place-items-center text-amber-700 shrink-0"><AlertCircle size={16} /></div>
                      <div className="text-[12.5px]">
                        <span className="font-semibold">No API key configured.</span> Get one free at{" "}
                        <a className="underline text-[var(--accent)]" href="https://resend.com/api-keys" target="_blank" rel="noreferrer">resend.com/api-keys</a> (3K emails/mo, no card).
                      </div>
                    </>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center gap-1.5"><KeyRound size={11} /> Resend API Key</label>
                    <Input
                      data-testid="settings-resend-key"
                      type="password"
                      autoComplete="off"
                      value={keyInput}
                      onChange={(e) => setKeyInput(e.target.value)}
                      placeholder={keyMasked ? `Saved · ${keyMasked}` : "re_xxxxxxxxxxxxxxxxxxxxx"}
                    />
                    <div className="text-[10.5px] text-[var(--muted)] mt-1">Leave blank to keep the existing key. {resendConfigured && "Type a new value to rotate."}</div>
                  </div>
                  <div>
                    <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Mode</label>
                    <Select
                      testid="settings-resend-mode"
                      options={[
                        { value: "test", label: "Test (sandbox · onboarding@resend.dev)" },
                        { value: "prod", label: "Production (verified domain sender)" },
                      ]}
                      value={form.resend_mode}
                      onChange={(v) => setForm({ ...form, resend_mode: v })}
                    />
                    <div className="text-[10.5px] text-[var(--muted)] mt-1 leading-relaxed">
                      {isProd ? (
                        <>Sends from <span className="num">{form.sender_email || "(set Sender Email below)"}</span> — domain must be verified at <a className="underline text-[var(--accent)]" href="https://resend.com/domains" target="_blank" rel="noreferrer">resend.com/domains</a>.</>
                      ) : (
                        <>Sends from <span className="num">onboarding@resend.dev</span> · only delivers to your Resend account email.</>
                      )}
                    </div>
                  </div>
                </div>

                <div className="border-t border-[var(--line)] pt-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold mb-2">Quick test</div>
                  <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end">
                    <div className="flex-1">
                      <Input
                        data-testid="settings-test-email"
                        type="email"
                        placeholder="your-resend-account-email@example.com"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                      />
                    </div>
                    <Btn variant="ghost" testid="settings-send-test" onClick={sendTest} disabled={testBusy || !resendConfigured}>
                      <Send size={13} /> {testBusy ? "Sending…" : "Send test email"}
                    </Btn>
                  </div>
                  <div className="text-[10.5px] text-[var(--muted)] mt-1">Save your settings first if you've just changed the key or mode.</div>
                </div>
              </div>
            </Section>

            <Section title="NK / RR Settlement Recipients" testid="settings-nkrr">
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center gap-1.5">
                    NK Email <span className="px-1 text-[10px] font-bold bg-sky-50 text-sky-800 border border-sky-200 rounded-sm">NK</span>
                  </label>
                  <Input data-testid="settings-nk-email" type="email" value={form.nk_email} onChange={(e) => setForm({ ...form, nk_email: e.target.value })} placeholder="nk@example.com" />
                </div>
                <div>
                  <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider flex items-center gap-1.5">
                    RR Email <span className="px-1 text-[10px] font-bold bg-violet-50 text-violet-800 border border-violet-200 rounded-sm">RR</span>
                  </label>
                  <Input data-testid="settings-rr-email" type="email" value={form.rr_email} onChange={(e) => setForm({ ...form, rr_email: e.target.value })} placeholder="rr@example.com" />
                </div>
              </div>
            </Section>

            <Section title="Sender Identity (Production mode)" testid="settings-sender">
              <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Sender Email</label>
                  <Input data-testid="settings-sender-email" type="email" value={form.sender_email} onChange={(e) => setForm({ ...form, sender_email: e.target.value })} placeholder="no-reply@yourdomain.com" disabled={!isProd} />
                  <div className="text-[10.5px] text-[var(--muted)] mt-1">Only used in production mode. Domain must be verified with Resend.</div>
                </div>
                <div>
                  <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Sender Name</label>
                  <Input data-testid="settings-sender-name" value={form.sender_name} onChange={(e) => setForm({ ...form, sender_name: e.target.value })} placeholder="BizAssist Online" />
                </div>
              </div>
            </Section>

            <div className="flex justify-end">
              <Btn variant="accent" testid="settings-save-email" onClick={save} disabled={busy}>
                <Mail size={14} /> {busy ? "Saving…" : "Save settings"}
              </Btn>
            </div>
          </>
        )}

        {tab === "access" && <PortalAccessTab />}

        {tab === "master" && <MasterDataTab />}

        {tab === "templates" && <EmailTemplatesTab />}

        {tab === "logs" && (
          <Section
            title="Email Send Log"
            testid="settings-email-log"
            right={<Btn variant="ghost" onClick={loadLog} disabled={logBusy}><RefreshCw size={12} className={logBusy ? "animate-spin" : ""} /> Refresh</Btn>}
          >
            <div className="overflow-x-auto max-h-[60vh]">
              <table className="table-data w-full">
                <thead><tr>
                  <th>Sent at</th>
                  <th>Type</th>
                  <th>Recipient</th>
                  <th>Reference</th>
                  <th>Summary</th>
                </tr></thead>
                <tbody>
                  {log.map((r) => {
                    const dt = r.ts ? new Date(r.ts) : null;
                    const dateLabel = dt ? `${dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} · ${dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}` : "—";
                    const kindTone =
                      r.kind === "Test Email" ? "bg-amber-50 text-amber-800 border-amber-200" :
                      r.kind === "Settlement Statement" ? "bg-sky-50 text-sky-800 border-sky-200" :
                      r.kind === "Partner Recon" ? "bg-violet-50 text-violet-800 border-violet-200" :
                      "bg-[var(--surface-2)] text-[var(--ink)] border-[var(--line-2)]";
                    return (
                      <tr key={r.id} data-testid={`email-log-row-${r.id}`}>
                        <td className="num text-[12px]">{dateLabel}</td>
                        <td><span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border rounded-sm ${kindTone}`}>{r.kind}</span></td>
                        <td className="text-[12px] num">{r.recipient || <span className="text-[var(--muted)]">—</span>}</td>
                        <td className="text-[11px] text-[var(--muted)] num">{r.subject}</td>
                        <td className="text-[11px] text-[var(--muted)] max-w-md truncate">{r.summary}</td>
                      </tr>
                    );
                  })}
                  {!log.length && <tr><td colSpan={5} className="text-center text-[var(--muted)] py-6 text-[12px]">No emails sent yet. Send a test email from the Email tab to validate the configuration.</td></tr>}
                </tbody>
              </table>
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}

// ---- Email Templates tab -------------------------------------------------
//
// Per-template editor. Edits are kept locally until "Save"; "Reset to default"
// reverts the field to the server-provided default copy (read-only). "Send
// preview to me" renders with example data and ships to the admin's email
// (or any address they enter) so they can sanity-check the actual delivered
// output before broadcasting.

function EmailTemplatesTab() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [draft, setDraft] = useState({ subject: "", html: "" });
  const [busy, setBusy] = useState(false);
  const [previewSendingTo, setPreviewSendingTo] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const [rendered, setRendered] = useState({ subject: "", html: "" });
  const subjectRef = useRef(null);
  const htmlRef = useRef(null);
  const lastFocusedRef = useRef("html");  // which field 'Insert variable' targets

  const load = async () => {
    try {
      const { data } = await api.get("/email-templates");
      setItems(data || []);
      if (data?.length && !activeId) setActiveId(data[0].id);
    } catch (e) {
      toast.error("Failed to load templates");
    }
  };
  useEffect(() => { load(); }, []);

  const active = items.find((t) => t.id === activeId);

  // Sync draft state when switching templates / loading.
  useEffect(() => {
    if (!active) return;
    setDraft({
      subject: active.subject || active.default_subject || "",
      html: active.html || active.default_html || "",
    });
    setPreviewSendingTo(user?.email || "");
  }, [activeId, items, active, user?.email]);

  // Live preview render — debounced via the template-preview endpoint so the
  // example values match exactly what the partner email will look like.
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/email-templates/preview", {
          template_id: active.id,
          subject: draft.subject,
          html: draft.html,
        });
        if (!cancelled) setRendered(data.rendered || { subject: "", html: "" });
      } catch (e) {
        // Phase 1 — debounced live-preview; transient failures (network
        // blips, schema validation while the admin is mid-edit) are
        // expected. Log to devtools so they're not invisible, but don't
        // surface a toast — the user is still typing.
        if (!cancelled) console.error("Settings: live preview render failed", e);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [active, draft]);

  const insertVar = (key) => {
    // With the WYSIWYG editor, the body uses @-mention for variables —
    // this helper now only targets the subject input. Clicking a chip
    // without subject focus inserts at end of subject (not body) so we
    // don't interfere with the chip-rich HTML inside the editor.
    const target = subjectRef.current;
    const cur = draft.subject;
    const start = (lastFocusedRef.current === "subject" && target?.selectionStart != null)
      ? target.selectionStart : cur.length;
    const end = (lastFocusedRef.current === "subject" && target?.selectionEnd != null)
      ? target.selectionEnd : cur.length;
    const insert = `{${key}}`;
    const next = cur.slice(0, start) + insert + cur.slice(end);
    setDraft({ ...draft, subject: next });
    setTimeout(() => {
      target?.focus();
      target?.setSelectionRange(start + insert.length, start + insert.length);
    }, 0);
  };

  const save = async () => {
    if (!active) return;
    setBusy(true);
    try {
      // Merge: preserve other templates that may exist server-side.
      const payload = {};
      items.forEach((t) => {
        if (t.id === active.id) {
          payload[t.id] = { subject: draft.subject, html: draft.html };
        } else if (t.subject || t.html) {
          payload[t.id] = { subject: t.subject, html: t.html };
        }
      });
      await api.put("/settings", { email_templates: payload });
      toast.success("Template saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const resetField = (field) => {
    if (!active) return;
    setDraft({ ...draft, [field]: active[`default_${field}`] || "" });
  };

  const sendPreview = async () => {
    if (!active) return;
    if (!previewSendingTo.trim()) { toast.error("Recipient email required"); return; }
    setPreviewBusy(true);
    try {
      const { data } = await api.post("/email-templates/preview", {
        template_id: active.id,
        subject: draft.subject,
        html: draft.html,
        to_email: previewSendingTo.trim(),
      });
      toast.success(`Preview sent to ${data.sent_to}`, { duration: 6000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview send failed", { duration: 8000 });
    }
    setPreviewBusy(false);
  };

  if (!items.length) {
    return <div className="p-6 text-[12.5px] text-[var(--muted)]">Loading…</div>;
  }

  return (
    <div className="space-y-4" data-testid="email-templates-tab">
      {/* Template selector — chips. Only "payout" today; designed to grow. */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {items.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setActiveId(t.id)}
            data-testid={`tpl-tab-${t.id}`}
            className={`text-[11.5px] px-3 py-1.5 rounded-sm border transition-colors ${
              t.id === activeId
                ? "bg-[var(--accent)] text-white border-[var(--accent)]"
                : "bg-[var(--surface-2)] text-[var(--ink-2)] border-[var(--line-2)] hover:border-[var(--ink-2)]"
            }`}
          >{t.label}</button>
        ))}
      </div>

      {active && (
        <Section title={active.label} testid={`tpl-${active.id}`}>
          <div className="p-5 space-y-4">
            <div className="text-[12.5px] text-[var(--muted)] leading-relaxed">{active.description}</div>

            {/* Variable picker — for the subject line. The body editor has
                its own @-mention flow (type @ inside it). */}
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold mb-1.5">Insert variable into subject</div>
              <div className="flex flex-wrap gap-1.5">
                {(active.variables || []).map((v) => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => insertVar(v.key)}
                    title={`${v.label} — example: ${v.example}`}
                    data-testid={`tpl-var-${v.key}`}
                    className="text-[11px] font-mono px-2 py-0.5 rounded-sm border border-[var(--line-2)] bg-[var(--surface)] hover:border-[var(--accent)] hover:text-[var(--accent)]"
                  >{`{${v.key}}`}</button>
                ))}
              </div>
              <div className="text-[10.5px] text-[var(--muted)] mt-1">Click to add to subject line. Inside the body, just type <b>@</b> — a picker will appear.</div>
            </div>

            {/* Subject */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Subject</label>
                <button type="button" onClick={() => resetField("subject")} data-testid="tpl-reset-subject"
                  className="text-[10.5px] text-[var(--muted)] hover:text-[var(--accent)] inline-flex items-center gap-1">
                  <RotateCcw size={10} /> Reset to default
                </button>
              </div>
              <Input
                ref={subjectRef}
                value={draft.subject}
                onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                onFocus={() => { lastFocusedRef.current = "subject"; }}
                data-testid="tpl-subject"
              />
              <div className="text-[10.5px] text-[var(--muted)] mt-1 font-mono truncate">Default: {active.default_subject}</div>
            </div>

            {/* HTML body — WYSIWYG */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">Body</label>
                <button type="button" onClick={() => resetField("html")} data-testid="tpl-reset-html"
                  className="text-[10.5px] text-[var(--muted)] hover:text-[var(--accent)] inline-flex items-center gap-1">
                  <RotateCcw size={10} /> Reset to default
                </button>
              </div>
              <VisualHtmlEditor
                value={draft.html}
                onChange={(html) => setDraft((d) => ({ ...d, html }))}
                variables={active.variables || []}
                testid="tpl-html"
              />
              <div className="text-[10.5px] text-[var(--muted)] mt-1">
                Toolbar for formatting · click a variable below OR type <b>@</b> inside the editor to pick a variable · click the <b>&lt;/&gt;</b> icon to see the raw HTML.
              </div>
            </div>

            {/* Live preview */}
            <div className="border border-[var(--line)] rounded-sm bg-[var(--surface-2)]">
              <div className="px-4 py-2 border-b border-[var(--line)] text-[10.5px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">
                Live preview · with sample data
              </div>
              <div className="p-4 space-y-3">
                <div className="text-[12.5px] text-[var(--ink)]"><span className="text-[var(--muted)]">Subject: </span>{rendered.subject}</div>
                <div className="border border-[var(--line)] bg-white rounded-sm p-4 text-[12.5px] text-[var(--ink-2)]"
                  data-testid="tpl-preview-html"
                  // Phase 1 — sanitize the rendered template HTML before
                  // injecting it into the preview. The template content is
                  // admin-authored, but defense-in-depth: a malicious paste
                  // (or a future seed import) shouldn't be able to execute
                  // scripts in the admin's session.
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      rendered.html || "<i style='color:#9CA3AF'>(empty)</i>",
                      {
                        USE_PROFILES: { html: true },
                        // Allow the chip styling we generate via VisualHtmlEditor.
                        ADD_ATTR: ["target", "rel", "data-var", "contenteditable"],
                      },
                    ),
                  }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-[var(--line)]">
              <Btn variant="accent" onClick={save} disabled={busy} testid="tpl-save">
                {busy ? "Saving…" : "Save template"}
              </Btn>
              <div className="flex-1" />
              <div>
                <label className="text-[10.5px] uppercase tracking-wider text-[var(--muted)] font-semibold">Send preview to</label>
                <Input
                  value={previewSendingTo}
                  onChange={(e) => setPreviewSendingTo(e.target.value)}
                  placeholder="you@example.com"
                  className="w-60"
                  data-testid="tpl-preview-recipient"
                />
              </div>
              <Btn variant="ghost" onClick={sendPreview} disabled={previewBusy} testid="tpl-preview-send">
                <SendIcon size={12} /> {previewBusy ? "Sending…" : "Send preview"}
              </Btn>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
}


// ---- Portal Access tab ---------------------------------------------------

function MasterDataTab() {
  const [lobs, setLobs] = useState([]);
  const [renaming, setRenaming] = useState(null);  // {oldName, newName}
  // Cascade-delete state. `deleting` is null when closed; otherwise:
  //   { name, stage: "loading" | "confirm" | "done", usage, removed }
  // The two-stage flow surfaces the impact preview BEFORE the destructive
  // confirm — admin can see "this LOB is referenced 4 places" and bail.
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/meta");
      setLobs(data.lobs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load master data");
    }
  };
  useEffect(() => { load(); }, []);

  const startRename = (oldName) => setRenaming({ oldName, newName: oldName });
  const submitRename = async () => {
    const oldName = renaming.oldName.trim();
    const newName = renaming.newName.trim();
    if (!newName) { toast.error("New name required"); return; }
    if (newName === oldName) { setRenaming(null); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/meta/rename-lob", { old_name: oldName, new_name: newName });
      const total = (data.programs_updated || 0) + (data.merchants_updated || 0) + (data.gateways_updated || 0) +
                    (data.partners_rate_cards_updated || 0) + (data.leads_eligible_lobs_updated || 0) + (data.leads_requested_lob_updated || 0);
      toast.success(`Renamed '${oldName}' → '${newName}' across ${total} rows`);
      setRenaming(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed", { duration: 8000 });
    }
    setBusy(false);
  };

  // ---- Cascade delete -----------------------------------------------------
  // Step 1: open modal, fetch usage counts ("Where is this LOB referenced?")
  const startDelete = async (name) => {
    setDeleting({ name, stage: "loading" });
    try {
      const { data } = await api.get(`/meta/lob-usage/${encodeURIComponent(name)}`);
      setDeleting({ name, stage: "confirm", usage: data.usage, total: data.total });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load usage");
      setDeleting(null);
    }
  };
  // Step 2: actually delete (after admin clicks the red button)
  const submitDelete = async () => {
    if (!deleting?.name) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/meta/lob/${encodeURIComponent(deleting.name)}`);
      setDeleting({ name: deleting.name, stage: "done", removed: data.removed, total: data.total });
      toast.success(`Removed '${deleting.name}' from ${data.total || 0} row${data.total === 1 ? "" : "s"} — won't reappear after redeploy.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed", { duration: 8000 });
    }
    setBusy(false);
  };
  const USAGE_LABELS = {
    programs: "Programs",
    merchants: "Merchants",
    gateways: "Gateway rate cards",
    partners_rate_cards: "Partner rate cards",
    leads_eligible: "Leads (eligible LOBs)",
    leads_requested: "Leads (requested LOB)",
    rate_versions: "Rate version history",
  };

  return (
    <div className="space-y-4" data-testid="master-data-tab">
      <Section title="Lines of Business (LOBs)" testid="master-lobs">
        <div className="p-5 space-y-3">
          <div className="text-[12px] text-[var(--muted)] leading-relaxed">
            LOBs are derived from your active programs, merchants, gateway rate-cards, partner rate-cards
            and leads. Renaming updates every reference instantly. <b>Deleting</b> removes the LOB everywhere
            in one shot — the only way to make a "deleted" LOB stay gone after a redeploy, since deleting
            from just one place lets the others keep it alive in the dropdown.
          </div>
          {lobs.length === 0 && <div className="text-[12px] text-[var(--muted)] italic">No LOBs in the system yet.</div>}
          <div className="flex flex-wrap gap-1.5" data-testid="lob-list">
            {lobs.map((lob) => (
              <div key={lob} className="inline-flex items-center gap-1.5 px-2 py-1 border border-[var(--line-2)] bg-[var(--surface-2)] rounded-sm text-[12px]" data-testid={`lob-chip-${lob}`}>
                <span className="text-[var(--ink-2)]">{lob}</span>
                <button
                  type="button"
                  onClick={() => startRename(lob)}
                  className="p-0.5 text-[var(--muted)] hover:text-[var(--accent)]"
                  title={`Rename '${lob}'`}
                  data-testid={`lob-rename-${lob}`}
                ><Edit2 size={11} /></button>
                <button
                  type="button"
                  onClick={() => startDelete(lob)}
                  className="p-0.5 text-[var(--muted)] hover:text-[var(--negative)]"
                  title={`Delete '${lob}' from every collection`}
                  data-testid={`lob-delete-${lob}`}
                ><Trash2 size={11} /></button>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {renaming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => !busy && setRenaming(null)}>
          <div className="bg-[var(--surface)] border border-[var(--line-2)] rounded-sm w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="lob-rename-modal">
            <div className="text-[16px] font-semibold mb-1">Rename LOB</div>
            <div className="text-[12px] text-[var(--muted)] mb-4">
              "{renaming.oldName}" will be replaced everywhere — programs, merchants, gateway rate-cards,
              partner rate-cards, and leads. This is instant and cannot be undone (re-rename to revert).
            </div>
            <label className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold">New name</label>
            <Input
              autoFocus
              value={renaming.newName}
              onChange={(e) => setRenaming({ ...renaming, newName: e.target.value })}
              data-testid="lob-rename-input"
            />
            <div className="flex justify-end gap-2 mt-5">
              <Btn variant="ghost" onClick={() => setRenaming(null)} disabled={busy}>Cancel</Btn>
              <Btn variant="accent" onClick={submitRename} disabled={busy || !renaming.newName.trim()} testid="lob-rename-submit">
                {busy ? "Renaming…" : "Rename"}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => !busy && setDeleting(null)}>
          <div className="bg-[var(--surface)] border border-[var(--line-2)] rounded-sm w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="lob-delete-modal">
            {deleting.stage === "loading" && (
              <div className="py-8 text-center text-[13px] text-[var(--muted)]" data-testid="lob-delete-loading">
                Checking where "<b>{deleting.name}</b>" is referenced…
              </div>
            )}
            {deleting.stage === "confirm" && (
              <>
                <div className="text-[16px] font-semibold mb-1">Delete LOB "{deleting.name}"?</div>
                {deleting.total === 0 ? (
                  <div className="text-[12.5px] text-[var(--muted)] mb-3" data-testid="lob-delete-noop-hint">
                    Not referenced anywhere — safe to delete. (If this LOB
                    is showing up after a redeploy, this delete will fully
                    clear it from any cached lookups.)
                  </div>
                ) : (
                  <>
                    <div className="text-[12.5px] text-[var(--muted)] mb-3">
                      This will remove "<b>{deleting.name}</b>" from <b className="text-[var(--ink)]">{deleting.total} row{deleting.total === 1 ? "" : "s"}</b> across the system:
                    </div>
                    <div className="border border-[var(--line)] rounded-sm overflow-hidden mb-4" data-testid="lob-delete-usage">
                      <table className="w-full text-[12.5px]">
                        <tbody>
                          {Object.entries(USAGE_LABELS).map(([key, label]) => {
                            const n = deleting.usage?.[key] || 0;
                            return (
                              <tr key={key} className="border-t border-[var(--line)] first:border-t-0">
                                <td className="py-1.5 px-3 text-[var(--ink-2)]">{label}</td>
                                <td className="py-1.5 px-3 text-right num font-semibold">
                                  {n > 0
                                    ? <span data-testid={`lob-usage-${key}`} className="text-[var(--negative)]">{n} affected</span>
                                    : <span className="text-[var(--muted)]">0</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                <div className="text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-2.5 mb-4">
                  <b>Programs and merchants</b> will keep their rows but lose this LOB tag (set to blank).
                  <b> Gateway and partner rate-cards</b> for this LOB will be deleted entirely.
                  This cannot be undone — you'd have to re-add the rate cards manually.
                </div>
                <div className="flex justify-end gap-2">
                  <Btn variant="ghost" onClick={() => setDeleting(null)} disabled={busy}>Cancel</Btn>
                  <Btn variant="danger" onClick={submitDelete} disabled={busy} testid="lob-delete-confirm">
                    {busy ? "Deleting…" : deleting.total === 0 ? "Confirm" : `Delete from ${deleting.total} row${deleting.total === 1 ? "" : "s"}`}
                  </Btn>
                </div>
              </>
            )}
            {deleting.stage === "done" && (
              <>
                <div className="text-[16px] font-semibold mb-3">
                  "{deleting.name}" deleted
                </div>
                <div className="text-[12.5px] text-[var(--muted)] mb-3" data-testid="lob-delete-done-summary">
                  Removed from <b className="text-[var(--ink)]">{deleting.total || 0} row{deleting.total === 1 ? "" : "s"}</b>.
                  This LOB <b>will not</b> reappear after a redeploy.
                </div>
                <div className="flex justify-end">
                  <Btn variant="accent" onClick={() => setDeleting(null)} testid="lob-delete-done">Done</Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PortalAccessTab() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/access/list");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load portal access");
      setRows([]);
    }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  // Iter31.2 — gentle 30s polling while the Portal Access tab is mounted
  // so the "Last seen" column stays fresh without a manual refresh.
  // Cleared automatically on tab switch (component unmounts).
  useEffect(() => {
    const id = setInterval(() => { load(); }, 30_000);
    return () => clearInterval(id);
  }, []);

  const revoke = async (u) => {
    if (!await openConfirm({
      title: `Revoke access for ${u.email}?`,
      message: "They'll be signed out immediately and won't be able to log in again until you re-grant access.",
      confirmLabel: "Revoke access",
      danger: true,
    })) return;
    try {
      await api.delete(`/admin/access/${u.id}`);
      toast.success(`Revoked access for ${u.email}`);
      setRows((rs) => (rs || []).filter((r) => r.id !== u.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't revoke");
    }
  };

  const fmtAgo = (iso) => {
    if (!iso) return "Never";
    const ms = Date.now() - new Date(iso).getTime();
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
    const month = Math.floor(day / 30);
    return `${month} mo ago`;
  };

  return (
    <Section
      title="Portal Access"
      testid="settings-portal-access"
      right={<Btn variant="ghost" onClick={load} disabled={busy}><RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh</Btn>}
    >
      <div className="p-4 sm:p-5 text-[12.5px] text-[var(--muted)] leading-relaxed border-b border-[var(--line)]">
        Generate access for partners and merchants from their respective pages. Each granted account is shown here with last-seen + revoke controls.
      </div>
      {rows === null ? (
        <div className="p-10 text-center text-[var(--muted)] text-[12.5px]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-10 text-center text-[var(--muted)] text-[12.5px]">No portal accounts granted yet. Use the <KeyRound size={11} className="inline -mt-0.5" /> icon on any Partner or Merchant row to create one.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table-data w-full" data-testid="portal-access-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Linked to</th>
                <th>Last seen</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} data-testid={`access-row-${u.id}`}>
                  <td className="font-mono text-[11.5px]">{u.email}</td>
                  <td>{u.name || "—"}</td>
                  <td>
                    <span className={`text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${
                      u.role === "partner" ? "bg-[var(--accent)]/8 text-[var(--accent)] border-[var(--accent)]/25" :
                      "bg-amber-50 text-amber-700 border-amber-300"
                    }`}>{u.role}</span>
                  </td>
                  <td className="text-[11.5px]">{u.linked_partner_id || u.linked_merchant_name || "—"}</td>
                  <td className="text-[11.5px] text-[var(--muted)]">{fmtAgo(u.last_login_at)}</td>
                  <td>
                    {u.must_change_password ? (
                      <span className="text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-300">Pending first login</span>
                    ) : u.status === "deactivated" ? (
                      <span className="text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-rose-50 text-rose-700 border border-rose-200">Deactivated</span>
                    ) : (
                      <span className="text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">Active</span>
                    )}
                  </td>
                  <td className="text-right">
                    <button
                      onClick={() => revoke(u)}
                      data-testid={`revoke-${u.id}`}
                      className="p-1.5 hover:bg-rose-50 text-[var(--negative)] rounded-sm"
                      title="Revoke access"
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}
